import { Injectable } from '@angular/core';
import { map, Observable, switchMap, throwError } from 'rxjs';
import {
  CreateReportDefinitionRequest,
  FileType,
  ReportConfig,
  ReportsService,
} from 'ldt-data-deliveries-api';
import { Ledger } from './ledger.types';
import {
  ContactSearchFilter,
  LedgerContactsService,
  SearchLedgerContactsRequest,
} from 'ldt-ledger-service-api-v2';
import * as moment from 'moment';
import * as FileSaver from 'file-saver';
import { AuthService } from '../auth/service/auth.service';

/**
 * Triggers ledger contact downloads through the data-deliveries service instead of the
 * synchronous ledger download endpoint, so large ledgers (millions of contacts) can be
 * exported asynchronously and fetched via signed URLs when ready.
 */
@Injectable({
  providedIn: 'root',
})
export class LedgerDownloadService {
  constructor(
    private reportsService: ReportsService,
    private ledgerContactsService: LedgerContactsService,
    private authService: AuthService
  ) {}

  /**
   * Creates a one-off data-deliveries report for the given ledger and filters. When no
   * destinationId is provided the export is a direct download, fetchable via signed URLs;
   * otherwise it is sent to the given configured destination.
   * @returns An Observable of the report delivery ID, for use with DownloadStatusService
   */
  downloadLedgerContacts(
    orgId: string,
    ledger: Ledger,
    filters: ContactSearchFilter[],
    fileType: FileType = FileType.Csv,
    destinationId?: number,
    operator?: SearchLedgerContactsRequest.OperatorEnum
  ): Observable<number> {
    // One name for both the report definition and the delivery: the backend derives the
    // exported filename and destination path from the report definition name, and the
    // delivery name is what the downloads list and status widget display.
    const name = LedgerDownloadService.buildExportName(ledger, filters);

    return this.reportsService.listReportSources(orgId).pipe(
      switchMap((sources) => {
        const ledgerSource = sources.find((source) => source.name.toLowerCase() === 'ledger');
        if (!ledgerSource) {
          return throwError(() => new Error('Ledger report source not found'));
        }

        const config = {
          ledger_id: ledger.id,
          filters: filters,
          // How the filters combine ('and' default / 'or'); passed through to the
          // ledger search the report runs
          operator: operator,
          include_person_record: false,
        } as unknown as ReportConfig;

        const createReportDefRequest: CreateReportDefinitionRequest = {
          name: name,
          source_id: ledgerSource.id,
          config: config,
        };

        return this.reportsService.createReportDefinition(orgId, createReportDefRequest);
      }),
      switchMap((reportDefinition) =>
        this.reportsService.deliver(orgId, undefined, {
          name: name,
          user_id: this.authService.getUserValue.id,
          report_definition_id: reportDefinition.id,
          file_type: fileType,
          destination_id: destinationId,
        })
      ),
      map((resp) => {
        if (!resp.report_delivery_id) {
          throw new Error('No report_delivery_id received from deliver');
        }
        return resp.report_delivery_id;
      })
    );
  }

  /**
   * Downloads ledger contacts synchronously through the ledger service and saves the CSV
   * in the browser. Suitable for small exports where the async delivery flow is overkill.
   */
  directDownloadLedgerContacts(
    orgId: string,
    ledger: Ledger,
    filters: ContactSearchFilter[],
    operator?: SearchLedgerContactsRequest.OperatorEnum
  ): Observable<void> {
    const name = LedgerDownloadService.buildExportName(ledger, filters);
    const searchRequest: SearchLedgerContactsRequest = { filters: filters, operator: operator };

    return this.ledgerContactsService.downloadLedgerContacts(ledger.id, orgId, searchRequest).pipe(
      map((response: any) => {
        const data = new Blob([response], { type: 'text/csv' });
        FileSaver.saveAs(data, name + '.csv');
      })
    );
  }

  private static buildExportName(ledger: Ledger, filters: ContactSearchFilter[]): string {
    const scope = filters.length === 0 ? 'all' : 'filtered';
    return (
      LedgerDownloadService.sanitizeForFilename(ledger.name) +
      '_' +
      scope +
      '_' +
      moment().format('YYYYMMDD-HHmmss')
    );
  }

  /**
   * Makes a ledger name safe for use in filenames and bucket paths: lowercased, runs of
   * anything but letters and digits collapsed to single dashes, capped in length.
   */
  private static sanitizeForFilename(name: string | null | undefined): string {
    const sanitized = (name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60)
      .replace(/-+$/, '');
    return sanitized || 'ledger';
  }
}
